// The user guide – shown on the public /guide page and inside the app (Help & guide).
// Screens of the app are real screenshots; the Google Cloud steps are drawn
// illustrations (Google's console changes often, so labels matter more than pixels).

import Link from "next/link";
import { Phone, Screen } from "./Frames";
import { APP, CHANGELOG, VERSION, shot } from "./site";

const TOC = [
  ["start", "1. Get started in 5 minutes"],
  ["profile", "2. Business profile, stamp & signature"],
  ["quotes", "3. Quotations"],
  ["lines", "4. Line items that fill themselves"],
  ["send", "5. Send, approval links & follow-ups"],
  ["stages", "6. Payment schedule & stage invoices"],
  ["invoices", "7. Invoices, payments & GST"],
  ["letters", "8. Letterhead & letters"],
  ["drive", "9. Connect Google Drive (step by step)"],
  ["backup", "10. Backups, login & security"],
  ["tips", "11. Shortcuts & tips"],
  ["changes", "What's new"],
];

function H({ id, children, sub }) {
  return (
    <div id={id} className="scroll-mt-24 pt-14 first:pt-0">
      <h2 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-[28px]">{children}</h2>
      {sub && <p className="mt-2 max-w-3xl text-[15px] leading-relaxed text-slate-600">{sub}</p>}
    </div>
  );
}

function Steps({ items }) {
  return (
    <ol className="mt-5 space-y-3">
      {items.map((s, i) => (
        <li key={i} className="flex gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-600 text-xs font-bold text-white">{i + 1}</span>
          <span className="pt-0.5 text-[15px] leading-relaxed text-slate-700">{s}</span>
        </li>
      ))}
    </ol>
  );
}

function Tip({ children, tone = "info" }) {
  const cls = tone === "warn" ? "bg-amber-50 text-amber-900 ring-amber-200" : "bg-sky-50 text-sky-900 ring-sky-200";
  return <div className={`mt-5 rounded-xl px-4 py-3 text-sm leading-relaxed ring-1 ${cls}`}>{children}</div>;
}

const K = ({ children }) => <kbd className="rounded border border-slate-300 bg-white px-1.5 py-0.5 font-mono text-[12px] text-slate-700 shadow-sm">{children}</kbd>;
const B = ({ children }) => <b className="font-semibold text-slate-900">{children}</b>;
const Code = ({ children }) => <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[13px] text-slate-800">{children}</code>;

// ---------- illustrated Google Cloud console steps ----------
function Console({ title, children, step }) {
  return (
    <figure className="overflow-hidden rounded-xl bg-white shadow-card ring-1 ring-slate-200">
      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2">
        <span className="h-2 w-2 rounded-full bg-slate-300" />
        <span className="h-2 w-2 rounded-full bg-slate-300" />
        <span className="h-2 w-2 rounded-full bg-slate-300" />
        <span className="ml-2 truncate rounded bg-white px-2 py-0.5 text-[10px] text-slate-400 ring-1 ring-slate-200">console.cloud.google.com</span>
      </div>
      <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 text-[11px] text-slate-600">
        <span className="font-semibold text-slate-800">Google Cloud</span>
        <span className="rounded border border-slate-200 px-1.5 py-0.5">Invoice Studio ▾</span>
        <span className="ml-auto text-slate-400">{title}</span>
      </div>
      <div className="p-4 text-[12px] text-slate-700">{children}</div>
      <figcaption className="border-t border-slate-100 bg-slate-50 px-3 py-1.5 text-[11px] text-slate-500">Step {step} · illustration – Google’s screens may look slightly different</figcaption>
    </figure>
  );
}
const Hl = ({ children }) => <span className="rounded bg-yellow-100 px-1 font-semibold text-slate-900 ring-1 ring-yellow-300">{children}</span>;
const Btn = ({ children }) => <span className="inline-block rounded bg-[#1a73e8] px-2.5 py-1 text-[11px] font-medium text-white">{children}</span>;
const FieldBox = ({ label, value, hl }) => (
  <div className="mt-2">
    <div className="text-[10px] text-slate-500">{label}</div>
    <div className={`mt-0.5 rounded border px-2 py-1 font-mono text-[11px] ${hl ? "border-yellow-400 bg-yellow-50" : "border-slate-300"}`}>{value}</div>
  </div>
);

function DriveConsoleSteps() {
  return (
    <div className="mt-6 grid gap-4 md:grid-cols-2">
      <Console title="New project" step={1}>
        <div className="font-semibold">New Project</div>
        <FieldBox label="Project name *" value="Invoice Studio" hl />
        <div className="mt-3 flex justify-end">
          <Btn>CREATE</Btn>
        </div>
      </Console>
      <Console title="APIs & Services › Library" step={2}>
        <div className="rounded border border-slate-300 px-2 py-1 text-slate-400">🔍 Google Drive API</div>
        <div className="mt-3 flex items-center gap-3 rounded-lg border border-slate-200 p-2">
          <span className="flex h-8 w-8 items-center justify-center rounded bg-slate-100 text-base">▲</span>
          <div className="flex-1">
            <div className="font-semibold">Google Drive API</div>
            <div className="text-[10px] text-slate-500">Google Enterprise API</div>
          </div>
          <Btn>ENABLE</Btn>
        </div>
      </Console>
      <Console title="Google Auth Platform › Branding / Audience" step={3}>
        <FieldBox label="App name" value="Invoice Studio" />
        <FieldBox label="User support email" value="you@gmail.com" />
        <div className="mt-2 text-[10px] text-slate-500">Audience</div>
        <div className="mt-0.5 flex gap-3">
          <span>○ Internal</span>
          <Hl>● External</Hl>
        </div>
        <div className="mt-2 text-[10px] text-slate-500">Test users</div>
        <div className="mt-0.5 flex items-center justify-between rounded border border-yellow-400 bg-yellow-50 px-2 py-1 font-mono text-[11px]">
          you@gmail.com <span className="text-[10px] text-slate-500">+ ADD USERS</span>
        </div>
      </Console>
      <Console title="Clients › Create client" step={4}>
        <FieldBox label="Application type" value="Web application" hl />
        <FieldBox label="Name" value="Invoice Studio web" />
        <div className="mt-2 text-[10px] text-slate-500">Authorised JavaScript origins</div>
        <div className="mt-0.5 rounded border border-yellow-400 bg-yellow-50 px-2 py-1 font-mono text-[11px]">https://your-invoice-site.com</div>
        <div className="mt-3 flex justify-end">
          <Btn>CREATE</Btn>
        </div>
      </Console>
      <Console title="OAuth client created" step={5}>
        <div className="font-semibold">OAuth client created</div>
        <FieldBox label="Client ID" value="1234567890-abc123def.apps.googleusercontent.com  ⧉" hl />
        <div className="mt-2 text-[10px] text-slate-500">You don’t need the client secret – the app runs in your browser.</div>
      </Console>
      <div className="flex flex-col justify-center rounded-xl bg-gradient-to-br from-brand-50 to-white p-5 ring-1 ring-brand-100">
        <div className="text-sm font-semibold text-slate-900">Step 6 – back in the app</div>
        <p className="mt-1 text-sm leading-relaxed text-slate-600">
          Settings → <B>Data &amp; backup</B> → Google Drive sync → paste the Client ID → <B>Connect Google Drive</B> → choose your Google account → <B>Allow</B>. Done – the screenshot below is the real screen.
        </p>
      </div>
    </div>
  );
}

export default function HelpGuide({ inApp = false }) {
  return (
    <div className="grid gap-10 lg:grid-cols-[240px_minmax(0,1fr)]">
      <nav className="hidden lg:block">
        <div className="sticky top-24 space-y-0.5 text-sm">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Guide · v{VERSION}</div>
          {TOC.map(([id, label]) => (
            <a key={id} href={`#${id}`} className="block rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-slate-900">
              {label}
            </a>
          ))}
        </div>
      </nav>

      <div className="min-w-0 max-w-4xl" data-testid="help-guide">
        <div className="mb-8 grid gap-2 sm:grid-cols-2 lg:hidden">
          {TOC.map(([id, label]) => (
            <a key={id} href={`#${id}`} className="rounded-lg bg-white px-3 py-2 text-sm text-slate-700 ring-1 ring-slate-200">
              {label}
            </a>
          ))}
        </div>

        <H id="start" sub="Everything runs in your browser – there’s nothing to install and no account to create with us. Your data stays on your device (and, if you want, in your own Google Drive).">
          1. Get started in 5 minutes
        </H>
        <Steps
          items={[
            <>Open the app and press <B>Continue with Google</B>. Your data is encrypted (AES-256) and opens on any device where you sign in with the same Google account. Prefer a <B>user ID and password</B>? That works too – or skip and add a login later in Settings → Security.</>,
            <>Go to <B>Settings → Business profiles</B> and fill in your business name, address, GSTIN/VAT, bank details, logo, signature and stamp. Pick your <B>business type</B>.</>,
            <>Add your first client (<B>Clients → + New client</B>) – or just type a new client while creating a quotation.</>,
            <>Create a quotation from <B>Quotations</B> or an invoice with <B>+ New invoice</B>, then download the PDF or send it on WhatsApp / email.</>,
            <>Press <B>Connect Google Drive</B> (Settings → Data &amp; backup) so the same data appears on your laptop, phone and every browser.</>,
          ]}
        />
        <div className="mt-6">
          <Screen src={shot("dashboard")} alt="Dashboard of the app" url="Dashboard" />
        </div>

        <H id="profile" sub="Everything on your documents – name, logo, colour, address, tax IDs, bank details, signature and stamp – comes from the business profile. Change it once and every quotation, invoice and letter uses it.">
          2. Business profile, stamp &amp; signature
        </H>
        <Steps
          items={[
            <>Settings → <B>Business profiles</B> → <B>Edit</B>. Add more profiles if you trade under more than one name or GSTIN.</>,
            <><B>Business type</B> decides which quotation templates and options you see. An IT company gets milestones and optional add-ons; a solar EPC gets Supply / Installation sections, prices including GST and a 25/70/5 payment schedule. You can still switch any option on per document.</>,
            <><B>Company stamp / seal</B>: upload a scan or photo (600 × 600 px or larger). It is kept in HD (up to 1600 px). Tap <B>Remove white background</B> for a clean, transparent stamp. Choose whether it prints on invoices &amp; quotations and on letters.</>,
            <><B>Signature</B>: also kept in HD, with the same background remover.</>,
            <>Pick your brand colour, default document design and default letterhead.</>,
          ]}
        />
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <Screen src={shot("profile")} alt="Business profile with business type" url="Settings › Business profiles" />
          <Screen src={shot("profile-stamp")} alt="HD company stamp settings" url="Settings › Business profiles" />
        </div>

        <H id="quotes" sub="Start from a template made for your industry – the subject, typical scope lines, terms and options are filled in. Add your prices and send.">
          3. Quotations
        </H>
        <Steps
          items={[
            <>Open <B>Quotations</B>. Templates for your business type appear first (“Show all industries” shows the rest).</>,
            <>Pick one – e.g. Solar, IT &amp; software, Construction, Interior, Trading, Events, Logistics, AMC, Consulting, Training or General.</>,
            <>Choose the client, adjust lines and prices, and press <B>Save &amp; finalise</B> (<K>Ctrl</K> + <K>S</K>).</>,
            <>Track every quote: open, accepted, declined, expiring soon, and when you last followed up. <B>Convert to invoice</B> in one click – only the optional items the client chose are carried over.</>,
          ]}
        />
        <div className="mt-6 grid gap-4">
          <Screen src={shot("quotations")} alt="Quotations page" url="Quotations" />
          <Screen src={shot("editor")} alt="Quotation editor with options" url="Quotations › Edit" />
        </div>

        <H id="lines" sub="Options sit above the line items as chips – switch on only what this document needs.">
          4. Line items that fill themselves
        </H>
        <ul className="mt-5 grid gap-3 text-[15px] text-slate-700 sm:grid-cols-2">
          {[
            ["Type-ahead", "Type a few letters – saved services and anything you billed before appear with price, HSN/SAC and tax. Press Enter to fill."],
            ["☆ Save as service", "Star a line to reuse it on any document."],
            ["Details", "“+ details” adds specification lines printed under the item."],
            ["Drag to reorder", "Drag ⠿ (or press ↑ / ↓). The app then keeps your order; “Sort A–Z” switches back."],
            ["Sections", "Group lines under headings like Supply / Installation with subtotals."],
            ["Optional items", "Add-ons on a quote that aren’t in the total until the client picks them."],
            ["Product photos", "A small photo on each line – great for furniture, interiors and trading."],
            ["Line discount & prices incl. tax", "Discount % per line; type GST-inclusive prices and the taxable value is worked out, with CGST/SGST per rate."],
          ].map(([t, d]) => (
            <li key={t} className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
              <div className="font-semibold text-slate-900">{t}</div>
              <div className="mt-1 text-sm leading-relaxed text-slate-600">{d}</div>
            </li>
          ))}
        </ul>
        <div className="mt-6">
          <Screen src={shot("autocomplete")} alt="Type-ahead suggestions for services" url="New invoice" />
        </div>

        <H id="send" sub="Send a link the client can open on any phone – they see the quote, tick add-ons and accept by WhatsApp or email. Nothing is uploaded anywhere: the quote travels inside the link.">
          5. Send, approval links &amp; follow-ups
        </H>
        <Steps
          items={[
            <>Open a quotation → <B>Send approval link</B>. Check the message and press <B>WhatsApp</B> or <B>Open in email app</B>.</>,
            <>The client sees your quotation, ticks optional items, types their name and taps <B>Accept on WhatsApp</B> or <B>Accept by email</B>.</>,
            <>When the reply arrives, tick the options they chose and press <B>Mark accepted</B>.</>,
            <>Not heard back? <B>Follow up</B> on the Quotations page sends a polite reminder with the link again.</>,
          ]}
        />
        <div className="mt-6 grid items-start gap-6 md:grid-cols-[1fr_auto]">
          <Screen src={shot("approval-compose")} alt="Send approval link dialog" url="Quotation › Send approval link" />
          <div className="flex justify-center gap-4">
            <Phone src={shot("client-phone")} alt="What the client sees" />
          </div>
        </div>
        <Tip>Your phone number in the business profile must include the country code (e.g. +91…) for the client’s WhatsApp button to reach you.</Tip>

        <H id="stages" sub="Switch on “Payment schedule” to print stages with amounts. From the saved quotation, raise an invoice for each stage when it’s due.">
          6. Payment schedule &amp; stage invoices
        </H>
        <Steps
          items={[
            <>In the editor, turn on <B>Payment schedule</B> and set the stages (e.g. 25% advance, 70% before dispatch, 5% on commissioning).</>,
            <>Open the saved quotation – each stage has an <B>Invoice stage</B> button. The invoice is created with every line at that stage’s share.</>,
            <>Invoiced stages show their invoice number, so nothing is billed twice.</>,
          ]}
        />
        <div className="mt-6">
          <Screen src={shot("quote-view")} alt="Quotation with payment schedule" url="Quotation" />
        </div>

        <H id="invoices" sub="GST (CGST + SGST / IGST), export under LUT, VAT / sales tax, credit notes and proforma invoices – with PDF, Excel and print.">
          7. Invoices, payments &amp; GST
        </H>
        <ul className="mt-5 list-disc space-y-2 pl-5 text-[15px] leading-relaxed text-slate-700">
          <li><B>Tax is chosen for you</B> from where the client is: CGST + SGST in your state, IGST outside, export (LUT) abroad.</li>
          <li><B>Record payment</B> (with TDS), send <B>payment reminders</B>, issue <B>credit notes</B>, and see what’s overdue on the dashboard.</li>
          <li><B>Merge</B> several invoices for one client – the new invoice gets a combined number like <Code>AS/2026-27/12-13</Code>.</li>
          <li><B>Month-end billing run</B> bills every retainer and logged sale in one go.</li>
          <li><B>Designs</B>: Executive, Royal, Modern, Elegant (premium) and GST Standard, Clean, Classic, Minimal, Corporate, Compact (simple).</li>
          <li><B>Import</B> past invoices from PDF, scans, Excel or CSV – the table, client, tax and totals are read automatically.</li>
        </ul>
        <div className="mt-6">
          <Screen src={shot("editor-preview")} alt="Invoice preview" url="Invoice › Preview" />
        </div>

        <H id="letters" sub="Write on your own letterhead – or download a blank letterhead PDF to print stationery.">
          8. Letterhead &amp; letters
        </H>
        <Steps
          items={[
            <>Open <B>Letterhead</B>, pick one of 8 designs (Executive, Modern Wave, Sidebar, Royal, Corporate, Classic, Minimal, Centred) and press <B>Write</B> – or <B>Blank PDF</B> for printing.</>,
            <>Start from a ready-made letter: quotation cover letter, payment reminder, work completion certificate, offer letter, experience certificate, NOC and more. Client and document details are filled in.</>,
            <>Add your signature, HD stamp, a QR code (website or Google reviews) and a faint logo watermark. Long letters repeat the letterhead on every page.</>,
          ]}
        />
        <div className="mt-6 grid gap-4">
          <Screen src={shot("letterhead")} alt="Letterhead designs" url="Letterhead" />
          <Screen src={shot("letter-editor")} alt="Letter editor with live preview" url="Letterhead › Letter" />
        </div>

        <H id="drive" sub="Google Drive keeps one data file in your own Drive so every device shows the same data. The app uses Google’s narrow “drive.file” permission – it can only see the file it creates, nothing else in your Drive.">
          9. Connect Google Drive (step by step)
        </H>
        <div className="mt-5 rounded-2xl bg-emerald-50 p-5 ring-1 ring-emerald-200">
          <div className="font-semibold text-emerald-900">Using the app on our website? There is nothing to set up.</div>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-[15px] text-emerald-900">
            <li>Settings → <B>Data &amp; backup</B> → <B>Connect Google Drive</B>.</li>
            <li>Choose your Google account and press <B>Allow</B>. Done.</li>
            <li>On your other devices: open the app, press <B>Continue with Google</B> (or Connect Google Drive) with the same Google account – your data appears.</li>
          </ol>
        </div>
        <p className="mt-6 text-[15px] text-slate-600">
          <B>Running your own copy of the app on your own domain?</B> Then create your own Google OAuth Client ID once, as below (about 5 minutes). If the box for a Client ID doesn’t appear in Settings, the site already has one built in.
        </p>
        <Steps
          items={[
            <>Go to <a className="font-semibold text-brand-700 underline" href="https://console.cloud.google.com/" target="_blank" rel="noreferrer">console.cloud.google.com</a> and sign in with the Google account whose Drive you want to use. Open the project menu → <B>New project</B> → name it “Invoice Studio” → <B>Create</B>.</>,
            <><B>APIs &amp; Services → Library</B> → search <B>Google Drive API</B> → <B>Enable</B>.</>,
            <><B>Google Auth Platform</B> (called “OAuth consent screen” in older consoles) → <B>Get started</B>: app name “Invoice Studio”, your support email, audience <B>External</B>, your contact email → <B>Create</B>. Then under <B>Audience → Test users</B> add every Gmail address that will use the app (or press <B>Publish app</B>).</>,
            <><B>Clients</B> (or Credentials → Create credentials → <B>OAuth client ID</B>) → Application type <B>Web application</B> → under <B>Authorised JavaScript origins</B> add the exact address you open the app on, e.g. <Code>https://your-invoice-site.com</Code> (no path, no trailing slash). Add <Code>http://localhost:3000</Code> too if you run it on your computer → <B>Create</B>.</>,
            <>Copy the <B>Client ID</B> (it ends with <Code>.apps.googleusercontent.com</Code>). You don’t need the client secret.</>,
            <>In the app: <B>Settings → Data &amp; backup → Google Drive sync</B> → paste the Client ID → <B>Connect Google Drive</B> → choose your account → <B>Allow</B>.</>,
            <>On your other devices, open the same site and press <B>Connect Google Drive</B> with the same Client ID and Google account. If you use a login, sign in with the same user ID and password to unlock the synced data.</>,
          ]}
        />
        <DriveConsoleSteps />
        <div className="mt-6">
          <Screen src={shot("drive-settings")} alt="Google Drive sync settings in the app" url="Settings › Data & backup" />
        </div>
        <div className="mt-6 overflow-hidden rounded-xl ring-1 ring-slate-200">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-[12px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5">If you see…</th>
                <th className="px-4 py-2.5">Do this</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {[
                ["“Error 400: origin_mismatch” / “not a valid origin”", "Add the exact address from your browser’s address bar (https://… with no path) to Authorised JavaScript origins. Changes can take a few minutes."],
                ["“Access blocked: app has not completed verification”", "Add your Gmail under Audience → Test users, or publish the app."],
                ["The Google window doesn’t open", "Allow pop-ups for the site and try again."],
                ["Another device shows “needs your login”", "Sign in with the same user ID and password used on the first device – the Drive file is encrypted with it."],
                ["You don’t want anyone to paste a Client ID", "Whoever hosts the site can set NEXT_PUBLIC_GOOGLE_CLIENT_ID when building it – the Connect button then works straight away."],
              ].map(([a, b]) => (
                <tr key={a}>
                  <td className="px-4 py-3 align-top font-medium text-slate-900">{a}</td>
                  <td className="px-4 py-3">{b}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <H id="backup" sub="There is no server holding your data – so you are in control of where copies live.">
          10. Backups, login &amp; security
        </H>
        <ul className="mt-5 list-disc space-y-2 pl-5 text-[15px] leading-relaxed text-slate-700">
          <li><B>Login with Google</B> (Settings → Security): your data is encrypted, and opens on any device where you sign in with the same Google account. Add family or staff the same way.</li>
          <li><B>Login with a user ID and password</B>: also encrypts everything on this device, in Google Drive and in backups. There is no password reset – write it down, or add Google sign-in as a spare way in.</li>
          <li><B>Your account</B> (Settings → General): your plan and renewal date. Only your account details reach our server – never your invoices.</li>
          <li><B>Download backup</B> (Settings → Data &amp; backup) saves one .json file you can restore anywhere.</li>
          <li><B>Auto-backup file</B> (Chrome / Edge): pick a file in your Google Drive / OneDrive / Dropbox folder and every change is written to it.</li>
          <li><B>Recycle bin</B>: deleted items stay for 30 days and can be restored.</li>
        </ul>
        <Tip tone="warn">Clearing your browser’s site data deletes the copy in that browser. Keep Google Drive sync or backups on.</Tip>

        <H id="tips">11. Shortcuts &amp; tips</H>
        <ul className="mt-5 grid gap-2 text-[15px] text-slate-700 sm:grid-cols-2">
          <li><K>Ctrl</K>/<K>⌘</K> + <K>K</K> – search everything and jump anywhere</li>
          <li><K>Ctrl</K>/<K>⌘</K> + <K>S</K> – save the document or letter</li>
          <li><K>Ctrl</K>/<K>⌘</K> + <K>B</K> – collapse the sidebar</li>
          <li>Settings → General → rename the tool to your own brand</li>
          <li>Install it like an app: browser menu → “Install” / “Add to Home screen”</li>
          <li>Works on phones – share PDFs straight to WhatsApp</li>
        </ul>
        {!inApp && (
          <div className="mt-8">
            <Link href={APP} className="inline-flex rounded-xl bg-brand-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-700">
              Open the app →
            </Link>
          </div>
        )}

        <H id="changes">What’s new</H>
        <div className="mt-5 space-y-5">
          {CHANGELOG.map((c) => (
            <div key={c.version} className="rounded-xl bg-white p-5 ring-1 ring-slate-200">
              <div className="flex items-baseline gap-3">
                <span className="rounded-full bg-slate-900 px-2.5 py-0.5 font-mono text-xs font-semibold text-white">v{c.version}</span>
                <span className="text-sm text-slate-500">{c.date}</span>
              </div>
              <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-slate-700">
                {c.items.map((i) => (
                  <li key={i}>{i}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="h-10" />
      </div>
    </div>
  );
}
